import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Filter,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Truck,
  Upload,
  MoreHorizontal,
  Trash2,
  Edit,
  Loader2,
} from "lucide-react";
import { getAccountDeliveryChallans, deleteAccountDeliveryChallan } from "../core/_requests";
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';

export default function AccountsSalesDeliveryChallans() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const filters = ["All", "Open", "Invoiced", "Closed"];
  
  const [challans, setChallans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);

  const fetchChallans = async () => {
    setIsLoading(true);
    try {
      const data = await getAccountDeliveryChallans();
      setChallans(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChallans();
  }, []);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);


  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this delivery challan?")) return;
    try {
      await deleteAccountDeliveryChallan(id);
      fetchChallans();
    } catch (err) {
      console.error("Failed to delete", err);
      alert("Failed to delete delivery challan");
    }
  };

  const filteredChallans = challans.filter(c => {
    if (activeFilter !== 'All') {
      if (activeFilter === 'Open' && c.status !== 'Open' && c.status !== 'Draft') return false;
      if (activeFilter === 'Invoiced' && c.status !== 'Invoiced') return false;
      if (activeFilter === 'Closed' && c.status !== 'Closed') return false;
    }
    if (searchQuery) {
      const sq = searchQuery.toLowerCase();
      if (!c.challanNumber?.toLowerCase().includes(sq) && !c.customerName?.toLowerCase().includes(sq)) {
        return false;
      }
    }
    return true;
  });

  const totalCount = challans.length;
  const openCount = challans.filter(c => c.status === 'Open' || c.status === 'Draft').length;
  const invoicedCount = challans.filter(c => c.status === 'Invoiced').length;
  
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const thisMonthCount = challans.filter(c => {
    if (!c.challanDate) return false;
    const d = new Date(c.challanDate);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).length;

  const stats = [
    { label: "Total Challans", value: totalCount.toString(), change: "+0%", trend: "up", color: "blue" },
    { label: "Open", value: openCount.toString(), change: "+0%", trend: "up", color: "amber" },
    { label: "Invoiced", value: invoicedCount.toString(), change: "0%", trend: "up", color: "emerald" },
    { label: "This Month", value: thisMonthCount.toString(), change: "0%", trend: "up", color: "violet" },
  ];

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleDownloadCSV = () => {
    if (filteredChallans.length === 0) {
      alert("No delivery challan data to export.");
      return;
    }
    const headers = ["Sr. No.", "Challan #", "Customer", "Date", "Type", "Status"];
    const rows = filteredChallans.map((challan, index) => [
      index + 1,
      challan.challanNumber || '-',
      challan.customerName || '-',
      formatDate(challan.challanDate),
      challan.challanType || '-',
      challan.status || 'Draft'
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `delivery_challans_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'invoiced':
        return 'bg-emerald-100 text-emerald-700';
      case 'draft':
        return 'bg-slate-100 text-slate-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-blue-100 text-blue-700';
    }
  };

  const challan = activeDropdown !== null
    ? filteredChallans.find((challan) => challan.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Sales
                </div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Delivery Challans</h1>
                <p className="text-xs text-slate-500">Track goods dispatched to customers</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchChallans} className="p-1 rounded hover:bg-slate-100 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.DELIVERY_CHALLAN_CREATE) && (
                  <button
                    onClick={() => navigate("/accounts/sales/delivery-challans/new")}
                    className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> New Challan
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}>
                      <Truck className={`w-4.5 h-4.5 text-${stat.color}-600`} />
                    </div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === "up" ? "text-emerald-600" : "text-red-500"}`}>
                      {stat.trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {stat.change}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map((f) => (
                    <button
                      key={f}
                      onClick={() => setActiveFilter(f)}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search challans..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                    <Filter className="w-4 h-4 text-slate-500" />
                  </button>
                  */}
                  <button 
                    onClick={handleDownloadCSV}
                    className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto pb-24 min-h-[300px]">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Challan #</th>
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Type</th>
                      <th className="px-4 py-3 font-medium text-center">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading delivery challans...</p>
                        </td>
                      </tr>
                    ) : filteredChallans.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5">
                              <Truck className="w-10 h-10" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                              {searchQuery ? 'No matching challans' : 'No delivery challans found'}
                            </h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">
                              {searchQuery ? 'Try adjusting your search or filters.' : 'Create delivery challans to track goods sent on approval or returnable basis.'}
                            </p>
                            {!searchQuery && (
                              hasPermission(ACCOUNTS_PERMS.DELIVERY_CHALLAN_CREATE) && (
                                <button
                                  onClick={() => navigate("/accounts/sales/delivery-challans/new")}
                                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                                >
                                  <Plus className="w-4 h-4" /> Create Challan
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredChallans.map((challan, index) => (
                        <tr key={challan.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.DELIVERY_CHALLAN_UPDATE) && navigate(`/accounts/sales/delivery-challans/${challan.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {challan.challanNumber || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {challan.customerName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(challan.challanDate)}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {challan.challanType || '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${getStatusBadge(challan.status)}`}>
                              {challan.status || 'Draft'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button 
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100" 
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === challan.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(challan.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
          </div>
        </main>
      </div>

      {challan && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.DELIVERY_CHALLAN_UPDATE) && (
                                  <button 
                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/accounts/sales/delivery-challans/${challan.id}`); }}
                                  >
                                    <Edit className="w-4 h-4 text-slate-400" /> Edit Challan
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.DELIVERY_CHALLAN_DELETE) && (
                                  <>
                                    <div className="h-px bg-slate-100 my-1"></div>
                                    <button 
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      onClick={(e) => handleDelete(e, challan.id)}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" /> Delete Challan
                                    </button>
                                  </>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
